// Settings › Returns (Settings-Returns.dc.html): the BargainFox returns
// portal's rules, stored in Postgres (returns_settings JSONB, defaults and
// validation in Portal-BE returnsSettings.js) and its reasons
// (return_reasons). Reads follow admin.settings.integrations; writes are
// Admin-only (fixed rule returns_config), so everyone else sees it read-only.
//
// One Save for the settings: only changed keys are PATCHed, and the API's
// field errors ({ errors: [{ key, message }] }) are shown next to their field
// (a service's errors next to that service). Reasons save as they change,
// each on its own call. Settings the code adds later (another builder's keys)
// still show, under "Other settings", with a plain editor for their type.
//
// Every section starts closed. A save that comes back with field errors (or
// is stopped by a field that isn't valid) opens the sections holding them and
// scrolls to the first, so an error is never left inside a closed section.
// Settings that point at a record (the return warehouse, its InPost account)
// are pick-lists of the API's `options`; an older BE without them gets text.
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Trash2, RotateCcw, Save } from "lucide-react";
import { useTranslation } from "../../i18n";
import {
    ReturnsSettingsPayload, ReturnReason, ReturnService, getReturnsSettings, patchReturnsSettings, getReturnReasons,
    createReturnReason, updateReturnReason, deleteReturnReason, fieldErrors, penceFrom,
} from "../../api/returns";
import RoyalMailLabelPool from "./RoyalMailLabelPool";
import "../../styles/admin-returns.css";

type Draft = Record<string, any>;
type TT = (k: string, p?: Record<string, string>) => string;

// Every key this screen has a place for; anything else lands in "Other settings".
const KNOWN = new Set([
    "enabled", "terms_url", "help_url", "return_warehouse", "return_address", "invoice_seller", "from_address", "reply_to", "support_inbox_email", "error_alert_email", "shipping_email_returns_link", "contact_via_gorgias",
    "window_days", "problem_window_days", "grace_days", "no_scan_days", "not_arrived_working_days",
    "returnless_damaged_pence", "returnless_wrong_pence", "returnless_change_of_mind_pence",
    "returnless_window_days", "returnless_rate_min_orders", "returnless_small_allowance",
    "returnless_max_return_rate_pct", "returnless_refunds", "returnless_replacements",
    "refund_original_delivery", "deduct_return_postage", "instant_replacement_limit_pence", "auto_approve_returns", "refund_review_limit_pence",
    "review_missing_requests", "missing_small_allowance",
    "exchanges_enabled", "exchange_min_stock", "exchange_express_pence", "exchange_tiers",
    "exchange_cancel_days", "ranking_window_days", "ranking_price_weight", "ranking_min_units", "top_seller_badges", "exchange_catalogue_refresh_minutes",
    "max_labels", "services", "label_pool_low_stock", "label_pool_max_age_days", "royal_mail_oba",
    "inpost_return_account", "inpost_drop_off_days", "inpost_locker_radius_km", "inpost_lockers_shown", "depot_dropoff_grace_days", "token_account_minutes", "token_email_days", "session_idle_minutes",
    "lookup_ip_failures", "lookup_order_failures", "captcha_after_failures", "mask_direct_sessions",
]);

// When an exchange tier moves the exchange order from Exchange to Unfulfilled.
// "booked" is retired: never before the customer has shipped the return.
const TIER_WHEN = ["shipped", "delivered", "received"];

/** Open every closed section around these elements, then bring the first into view. */
const reveal = (els: Element[], block: ScrollLogicalPosition = "center") => {
    for (const el of els) {
        for (let d = el.closest("details"); d; d = d.parentElement?.closest("details") ?? null) d.open = true;
    }
    els[0]?.scrollIntoView?.({ behavior: "smooth", block });
};

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));
const fmtPounds = (pence: number | null | undefined) => (pence == null ? "" : (pence / 100).toFixed(2));

// ---- inputs --------------------------------------------------------------------------------

/** A text box that reports a parsed value, or null while what's typed isn't one. */
const ParsedInput = ({ id, initial, parse, onValue, disabled, invalid, prefix, inputMode, ariaLabel, width }: {
    id?: string; initial: string; parse: (s: string) => number | null; onValue: (v: number | null) => void;
    disabled?: boolean; invalid?: boolean; prefix?: string; inputMode?: "numeric" | "decimal"; ariaLabel?: string; width?: number;
}) => {
    const [text, setText] = useState(initial);
    return (
        <span className={`rs-unit${prefix ? " rs-unit--prefix" : ""}`} style={width ? { width } : undefined}>
            {prefix && <span aria-hidden="true">{prefix}</span>}
            <input id={id} className={`rt-input${invalid ? " rt-input--error" : ""}`} inputMode={inputMode} value={text} disabled={disabled}
                aria-label={ariaLabel} aria-invalid={invalid || undefined}
                onChange={(e) => { setText(e.target.value); onValue(parse(e.target.value)); }} />
        </span>
    );
};

const parseInt0 = (s: string) => (/^\s*\d+\s*$/.test(s) ? Number(s.trim()) : null);
/** "61 x 46 x 46" (x, ×, * or commas) → [61, 46, 46]; null when it isn't three sizes. */
export const parseDims = (s: string): [number, number, number] | null => {
    const parts = s.split(/\s*(?:x|×|\*|,)\s*/i).map((p) => p.trim()).filter(Boolean);
    if (parts.length !== 3 || !parts.every((p) => /^\d+(\.\d+)?$/.test(p))) return null;
    const dims = parts.map(Number) as [number, number, number];
    return dims.every((d) => d >= 1 && d <= 1000) ? dims : null;
};
const fmtDims = (d: number[] | null | undefined) => (Array.isArray(d) && d.length === 3 ? d.join(" × ") : "");

/** A limit that may be left empty (the carrier's own): empty → undefined, else a number, else bad. */
const OptionalLimit = ({ initial, parse, onValue, disabled, invalid, ariaLabel, placeholder, width }: {
    initial: string; parse: (s: string) => unknown; onValue: (v: unknown, bad: boolean) => void;
    disabled?: boolean; invalid?: boolean; ariaLabel: string; placeholder: string; width: number;
}) => {
    const [text, setText] = useState(initial);
    return (
        <span className="rs-unit" style={{ width }}>
            <input className={`rt-input${invalid ? " rt-input--error" : ""}`} value={text} disabled={disabled} aria-label={ariaLabel}
                placeholder={placeholder} aria-invalid={invalid || undefined}
                onChange={(e) => {
                    setText(e.target.value);
                    if (!e.target.value.trim()) { onValue(undefined, false); return; }
                    const v = parse(e.target.value);
                    onValue(v, v === null);
                }} />
        </span>
    );
};
const parseNum = (s: string) => (/^\s*\d+(\.\d+)?\s*$/.test(s) ? Number(s.trim()) : null);
const patterns = (s: string) => s.split(/[,\n]/).map((p) => p.trim()).filter(Boolean);

const ReturnsSettings = ({ canEdit }: { canEdit: boolean }) => {
    const { t } = useTranslation();
    const [payload, setPayload] = useState<ReturnsSettingsPayload | null>(null);
    const [draft, setDraft] = useState<Draft>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [bad, setBad] = useState<Record<string, boolean>>({});
    const [version, setVersion] = useState(0); // remounts the text inputs on load / discard
    const [saving, setSaving] = useState(false);
    const [loadState, setLoadState] = useState<"loading" | "ok" | "denied" | "failed">("loading");
    const [reasons, setReasons] = useState<ReturnReason[]>([]);
    const rootRef = useRef<HTMLDivElement>(null);
    // Set when a save is refused: which flagged elements to reveal once they've rendered.
    const [revealing, setRevealing] = useState<{ selector: string } | null>(null);

    const load = useCallback(async () => {
        const [s, r] = await Promise.all([getReturnsSettings(), getReturnReasons()]);
        if (s.status === 403) { setLoadState("denied"); return; }
        if (!s.ok || !s.data) { setLoadState("failed"); return; }
        setPayload(s.data);
        setDraft(clone(s.data.settings));
        setErrors({});
        setBad({});
        setVersion((v) => v + 1);
        setReasons(r.ok && r.data ? r.data.reasons : []);
        setLoadState("ok");
    }, []);
    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        if (revealing && rootRef.current) reveal(Array.from(rootRef.current.querySelectorAll(revealing.selector)));
    }, [revealing]);

    // A deep link to a section (admin-nav: /admin/settings#returns-services) can
    // land before this has loaded, after the page's own hash handler gave up.
    useEffect(() => {
        if (loadState !== "ok") return;
        const id = window.location.hash.replace(/^#/, "");
        const el = id ? document.getElementById(id) : null;
        if (el && rootRef.current?.contains(el)) reveal([el], "start");
    }, [loadState]);

    const set = (key: string, value: unknown) => {
        setDraft((d) => ({ ...d, [key]: value }));
        setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    };
    const markBad = (key: string, isBad: boolean) => setBad((b) => (b[key] === isBad ? b : { ...b, [key]: isBad }));

    const changed = useMemo(() => {
        if (!payload) return [];
        return Object.keys(draft).filter((k) => !same(draft[k], (payload.settings as any)[k]));
    }, [draft, payload]);
    const anyBad = Object.values(bad).some(Boolean);

    const save = async () => {
        if (!payload || changed.length === 0) return;
        if (anyBad) { toast.error(t("rs_fix_fields")); setRevealing({ selector: "[aria-invalid='true']" }); return; }
        setSaving(true);
        try {
            const patch = Object.fromEntries(changed.map((k) => [k, draft[k]]));
            const r = await patchReturnsSettings(patch);
            if (r.ok && r.data) {
                setPayload({ ...payload, settings: r.data.settings });
                setDraft(clone(r.data.settings));
                setVersion((v) => v + 1);
                setErrors({});
                toast.success(t("rs_saved"));
            } else if (r.status === 400) {
                setErrors(fieldErrors(r.body));
                setRevealing({ selector: ".rt-field-error[role='alert']" });
                toast.error(t("rs_fix_fields"));
            } else if (r.status === 403) {
                toast.error(t("rs_admin_only"));
            } else {
                toast.error(r.message || t("rs_save_failed"));
            }
        } finally {
            setSaving(false);
        }
    };
    const discard = () => {
        if (!payload) return;
        setDraft(clone(payload.settings));
        setErrors({});
        setBad({});
        setVersion((v) => v + 1);
    };

    if (loadState === "loading") return <p className="rt-muted">{t("loading")}</p>;
    if (loadState === "denied") return <p className="rt-muted">{t("rs_no_access")}</p>;
    if (loadState === "failed" || !payload) {
        return <p className="rt-error">{t("rs_load_failed")} <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={load}>{t("rt_refresh")}</button></p>;
    }

    const ro = !canEdit;
    const err = (key: string) => errors[key] ? <p className="rt-field-error" role="alert">{errors[key]}</p> : null;

    // Field helpers -------------------------------------------------------------------------
    const intField = (key: string, labelKey: string, hintKey?: string) => (
        <Field key={key} id={`rs-${key}`} label={t(labelKey)} hint={hintKey ? t(hintKey) : undefined} error={err(key)}>
            <ParsedInput key={`${key}-${version}`} id={`rs-${key}`} initial={String(draft[key] ?? "")} parse={parseInt0} inputMode="numeric" disabled={ro}
                invalid={!!errors[key] || bad[key]} onValue={(v) => { markBad(key, v === null); if (v !== null) set(key, v); }} />
        </Field>
    );
    const numField = (key: string, labelKey: string, hintKey?: string) => (
        <Field key={key} id={`rs-${key}`} label={t(labelKey)} hint={hintKey ? t(hintKey) : undefined} error={err(key)}>
            <ParsedInput key={`${key}-${version}`} id={`rs-${key}`} initial={String(draft[key] ?? "")} parse={parseNum} inputMode="decimal" disabled={ro}
                invalid={!!errors[key] || bad[key]} onValue={(v) => { markBad(key, v === null); if (v !== null) set(key, v); }} />
        </Field>
    );
    // A day count that may be left empty (null): "the same as" another setting.
    const optionalIntField = (key: string, labelKey: string, hint: string, placeholder: string) => (
        <Field key={key} id={`rs-${key}`} label={t(labelKey)} hint={hint} error={err(key)}>
            <span className="rs-unit">
                <input key={`${key}-${version}`} id={`rs-${key}`} className={`rt-input${errors[key] || bad[key] ? " rt-input--error" : ""}`} inputMode="numeric" disabled={ro}
                    defaultValue={draft[key] == null ? "" : String(draft[key])} placeholder={placeholder} aria-invalid={!!errors[key] || bad[key] || undefined}
                    onChange={(e) => {
                        const s = e.target.value;
                        if (!s.trim()) { markBad(key, false); set(key, null); return; }
                        const v = parseInt0(s);
                        markBad(key, v === null);
                        if (v !== null) set(key, v);
                    }} />
            </span>
        </Field>
    );
    const moneyField = (key: string, labelKey: string, hintKey?: string) => (
        <Field key={key} id={`rs-${key}`} label={t(labelKey)} hint={hintKey ? t(hintKey) : undefined} error={err(key)}>
            <ParsedInput key={`${key}-${version}`} id={`rs-${key}`} initial={fmtPounds(draft[key])} parse={penceFrom} inputMode="decimal" prefix="£" disabled={ro}
                invalid={!!errors[key] || bad[key]} onValue={(v) => { markBad(key, v === null); if (v !== null) set(key, v); }} />
        </Field>
    );
    const textField = (key: string, labelKey: string, hintKey?: string, { nullable = true, mono = false } = {}) => (
        <Field key={key} id={`rs-${key}`} label={t(labelKey)} hint={hintKey ? t(hintKey) : undefined} error={err(key)}>
            <input id={`rs-${key}`} className={`rt-input${mono ? " rt-mono" : ""}${errors[key] ? " rt-input--error" : ""}`} disabled={ro}
                value={(draft[key] as string | null) ?? ""} aria-invalid={!!errors[key] || undefined}
                onChange={(e) => set(key, nullable && !e.target.value.trim() ? null : e.target.value.trim() === "" ? "" : e.target.value)} />
        </Field>
    );
    // A setting that names a record: one of `choices`. A stored value that isn't
    // one stays selected and shows as invalid (`unlisted` says why) until a person
    // picks another; it is never changed for them.
    const pickField = (key: string, labelKey: string, hintKey: string, choices: { value: string; label: string }[], unlisted: string) => {
        const v = (draft[key] as string | null) ?? "";
        const listed = choices.some((c) => c.value === v);
        const invalid = !!errors[key] || !listed;
        return (
            <Field key={key} id={`rs-${key}`} label={t(labelKey)} hint={t(hintKey)}
                error={err(key) ?? (!listed ? <p className="rt-field-error">{v === "" && choices.length ? t("rs_pick_one") : unlisted}</p> : null)}>
                <select id={`rs-${key}`} className={`rt-input${invalid ? " rt-input--error" : ""}`} value={v} disabled={ro}
                    aria-invalid={invalid || undefined} onChange={(e) => set(key, e.target.value)}>
                    {!listed && <option value={v} disabled>{v === "" ? t("rs_pick_one") : t("rs_not_listed", { value: v })}</option>}
                    {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
            </Field>
        );
    };
    const check = (key: string, labelKey: string, hintKey?: string) => (
        <label key={key} className="rs-check">
            <input type="checkbox" checked={draft[key] === true} disabled={ro} onChange={(e) => set(key, e.target.checked)} />
            <span><strong>{t(labelKey)}</strong>{hintKey && <small>{t(hintKey)}</small>}{err(key)}</span>
        </label>
    );
    const address = (key: string, nameField: "company" | "name", extra: string[] = []) => {
        const v = (draft[key] ?? {}) as Record<string, string | null>;
        const fields = [nameField, "line_1", "line_2", "city", "postcode", "country", ...extra];
        return (
            <div className="rs-address">
                {fields.map((f) => (
                    <label key={f} className="rs-field">
                        <span className="rt-label">{t(`rs_addr_${f}`)}</span>
                        <input className="rt-input" disabled={ro} value={v[f] ?? ""}
                            onChange={(e) => set(key, { ...v, [f]: e.target.value === "" && f === "line_2" ? null : e.target.value })} />
                    </label>
                ))}
                {err(key)}
            </div>
        );
    };

    // Services ------------------------------------------------------------------------------
    const services: ReturnService[] = Array.isArray(draft.services) ? draft.services : [];
    const setService = (id: string, patch: Partial<ReturnService>) => {
        set("services", services.map((s) => (s.id === id ? { ...s, ...patch } : s)));
        setErrors((e) => { const n = { ...e }; delete n[`services.${id}`]; delete n.services; return n; });
    };

    // Exchange tiers ------------------------------------------------------------------------
    const tiers: { under_pence: number | null; when: string }[] = Array.isArray(draft.exchange_tiers) ? draft.exchange_tiers : [];
    const setTier = (i: number, patch: Partial<{ under_pence: number | null; when: string }>) =>
        set("exchange_tiers", tiers.map((tier, j) => (j === i ? { ...tier, ...patch } : tier)));

    // Pick-lists: the return warehouse, and the InPost accounts AT that warehouse ----------
    const options = payload.options;
    const warehouse = String(draft.return_warehouse ?? "");
    const inpostAccounts = (options?.inpost_accounts ?? []).filter((a) => a.warehouse === warehouse);
    const warehouseField = options
        ? pickField("return_warehouse", "rs_return_warehouse", "rs_return_warehouse_hint",
            options.warehouses.map((w) => ({ value: w, label: w })), t("rs_warehouse_unknown", { value: warehouse }))
        : textField("return_warehouse", "rs_return_warehouse", "rs_return_warehouse_hint", { nullable: false });
    const inpostField = options
        ? pickField("inpost_return_account", "rs_inpost_account", "rs_inpost_account_hint",
            // Off for outbound labels is still a valid returns account (Pembroke's ooh): say so on the option.
            inpostAccounts.map((a) => ({ value: a.account_label, label: a.enabled ? a.account_label : t("rs_inpost_returns_only", { label: a.account_label }) })),
            inpostAccounts.length
                ? t("rs_inpost_not_at", { label: String(draft.inpost_return_account ?? ""), warehouse })
                : t("rs_inpost_none_at", { warehouse }))
        : textField("inpost_return_account", "rs_inpost_account", "rs_inpost_account_hint", { nullable: false, mono: true });

    const others = Object.keys(draft).filter((k) => !KNOWN.has(k)).sort();

    return (
        <div className="rs" ref={rootRef}>
            {payload.updated_at && (
                <p className="rt-muted rt-small">{t("rs_last_saved", { who: payload.updated_by ?? "-", when: new Date(payload.updated_at).toLocaleString() })}</p>
            )}
            {ro && <p className="rt-muted rt-small">{t("rs_read_only")}</p>}

            <Sub title={t("rs_portal")} id="returns-portal">
                <label className="rs-switch">
                    <input type="checkbox" checked={draft.enabled === true} disabled={ro} onChange={(e) => set("enabled", e.target.checked)} />
                    <span>{t("rs_enabled")}</span>
                </label>
                {err("enabled")}
                <div className="rs-grid">
                    {warehouseField}
                    {textField("terms_url", "rs_terms_url", "rs_url_hint")}
                    {textField("help_url", "rs_help_url", "rs_url_hint")}
                </div>
                <h4 className="rs-h4">{t("rs_return_address")}</h4>
                <p className="rt-muted rt-small">{t("rs_return_address_hint")}</p>
                {address("return_address", "company")}
            </Sub>

            <Sub title={t("rs_window")} id="returns-window">
                <div className="rs-grid">
                    {intField("window_days", "rs_window_days", "rs_window_days_hint")}
                    {optionalIntField("problem_window_days", "rs_problem_window_days",
                        t("rs_problem_window_days_hint", { days: String(draft.window_days ?? "") }), t("rs_same_as_window", { days: String(draft.window_days ?? "") }))}
                    {intField("grace_days", "rs_grace_days", "rs_grace_days_hint")}
                    {intField("no_scan_days", "rs_no_scan_days", "rs_no_scan_days_hint")}
                    {intField("not_arrived_working_days", "rs_not_arrived", "rs_not_arrived_hint")}
                </div>
            </Sub>

            <Sub title={t("rs_refunds")} id="returns-refunds">
                <div className="rs-grid">
                    {moneyField("returnless_damaged_pence", "rs_returnless_damaged", "rs_returnless_damaged_hint")}
                    {moneyField("returnless_wrong_pence", "rs_returnless_wrong", "rs_returnless_wrong_hint")}
                    {moneyField("returnless_change_of_mind_pence", "rs_returnless_mind", "rs_returnless_mind_hint")}
                </div>
                {/* Who may keep items at all, over a rolling window (Portal-BE returnMath.returnlessStandingOk). */}
                <h4 className="rs-h4">{t("rs_standing")}</h4>
                <div className="rs-grid">
                    {intField("returnless_window_days", "rs_standing_days", "rs_standing_days_hint")}
                    {intField("returnless_rate_min_orders", "rs_standing_min_orders", "rs_standing_min_orders_hint")}
                    {intField("returnless_small_allowance", "rs_standing_allowance", "rs_standing_allowance_hint")}
                    {numField("returnless_max_return_rate_pct", "rs_returnless_rate", "rs_returnless_rate_hint")}
                </div>
                <p className="rt-muted rt-small">{t("rs_return_rate_explained")}</p>
                <div className="rs-checks">
                    {check("returnless_refunds", "rs_returnless_refunds", "rs_returnless_refunds_hint")}
                    {check("returnless_replacements", "rs_returnless_replacements", "rs_returnless_replacements_hint")}
                    {check("refund_original_delivery", "rs_refund_delivery", "rs_refund_delivery_hint")}
                    {check("deduct_return_postage", "rs_deduct_postage", "rs_deduct_postage_hint")}
                </div>
            </Sub>

            <Sub title={t("rs_replacements")} id="returns-replacements">
                <p className="rt-muted rt-small">{t("rs_replacements_hint")}</p>
                <div className="rs-grid">
                    {moneyField("instant_replacement_limit_pence", "rs_instant_limit", "rs_instant_limit_hint")}
                    {moneyField("refund_review_limit_pence", "rs_refund_review_limit", "rs_refund_review_limit_hint")}
                </div>
                <div className="rs-checks">
                    {/* At or over the instant limit with the item coming back: no review, held until the return's first carrier scan. */}
                    {check("auto_approve_returns", "rs_auto_approve_returns", "rs_auto_approve_returns_hint")}
                </div>
                {/* Missing and not arrived: at once in good standing (Portal-BE returnMath.missingStandingOk), else reviewed whatever the value. */}
                <h4 className="rs-h4">{t("rs_missing")}</h4>
                <div className="rs-grid">
                    {intField("missing_small_allowance", "rs_missing_allowance", "rs_missing_allowance_hint")}
                </div>
                <div className="rs-checks">
                    {check("review_missing_requests", "rs_review_missing", "rs_review_missing_hint")}
                </div>
            </Sub>

            <Sub title={t("rs_exchanges")} id="returns-exchanges">
                <label className="rs-switch">
                    <input type="checkbox" checked={draft.exchanges_enabled === true} disabled={ro} onChange={(e) => set("exchanges_enabled", e.target.checked)} />
                    <span>{t("rs_exchanges_enabled")}</span>
                </label>
                <div className="rs-grid">
                    {intField("exchange_min_stock", "rs_min_stock", "rs_min_stock_hint")}
                    {/* The one limit on time in Exchange (exchange_hold_days, never read, is retired). */}
                    {intField("exchange_cancel_days", "rs_cancel_days", "rs_cancel_days_hint")}
                    {/* One fee whatever the basket weighs (Jack, 2026-11-03); FastFox members pay nothing. */}
                    {moneyField("exchange_express_pence", "rs_express_fee", "rs_express_fee_hint")}
                </div>
                <h4 className="rs-h4">{t("rs_tiers")}</h4>
                <p className="rt-muted rt-small">{t("rs_tiers_hint")}</p>
                <div className="rs-table-wrap">
                    <table className="rs-table">
                        <thead><tr><th>{t("rs_tier_value")}</th><th>{t("rs_tier_when")}</th></tr></thead>
                        <tbody>
                            {tiers.map((tier, i) => (
                                <tr key={i}>
                                    <td>
                                        {tier.under_pence === null
                                            ? t("rs_tier_and_over", { amount: fmtPounds(tiers[i - 1]?.under_pence ?? 0) })
                                            : (
                                                <span className="rs-inline">{t("rs_tier_under")}
                                                    <ParsedInput key={`tier-${i}-${version}`} initial={fmtPounds(tier.under_pence)} parse={penceFrom} prefix="£" inputMode="decimal"
                                                        disabled={ro} width={110} ariaLabel={t("rs_tier_limit", { n: String(i + 1) })} invalid={bad[`tier${i}`]}
                                                        onValue={(v) => { markBad(`tier${i}`, v === null); if (v !== null) setTier(i, { under_pence: v }); }} />
                                                </span>
                                            )}
                                    </td>
                                    <td>
                                        {/* A stored trigger that's no longer allowed (an old "booked") stays shown, marked, until changed. */}
                                        <select className={`rt-input${TIER_WHEN.includes(tier.when) ? "" : " rt-input--error"}`} value={tier.when} disabled={ro}
                                            aria-label={t("rs_tier_trigger", { n: String(i + 1) })} aria-invalid={!TIER_WHEN.includes(tier.when) || undefined}
                                            onChange={(e) => setTier(i, { when: e.target.value })}>
                                            {!TIER_WHEN.includes(tier.when) && (
                                                <option value={tier.when} disabled>{tier.when === "booked" ? t("rs_when_booked") : tier.when}</option>
                                            )}
                                            {TIER_WHEN.map((w) => <option key={w} value={w}>{t(`rs_when_${w}`)}</option>)}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {tiers.map((tier, i) => (TIER_WHEN.includes(tier.when) ? null : (
                    <p key={`retired-${i}`} className="rt-field-error">{t("rs_tier_when_retired", { n: String(i + 1) })}</p>
                )))}
                {err("exchange_tiers")}
                <h4 className="rs-h4">{t("rs_ranking")}</h4>
                <p className="rt-muted rt-small">{t("rs_ranking_hint")}</p>
                <div className="rs-grid">
                    {intField("ranking_window_days", "rs_ranking_days")}
                    <Field id="rs-ranking_price_weight" label={t("rs_price_weight")} hint={t("rs_price_weight_hint")} error={err("ranking_price_weight")}>
                        <span className="rs-range">
                            <input id="rs-ranking_price_weight" type="range" min={0} max={1} step={0.1} disabled={ro}
                                value={Number(draft.ranking_price_weight ?? 0)} onChange={(e) => set("ranking_price_weight", Number(e.target.value))} />
                            <span className="rt-mono">{Number(draft.ranking_price_weight ?? 0).toFixed(1)}</span>
                        </span>
                    </Field>
                    {intField("ranking_min_units", "rs_min_units")}
                    {intField("top_seller_badges", "rs_top_badges", "rs_top_badges_hint")}
                    {/* The shop's snapshot (Portal-BE migration 275): how often the job runner rebuilds it; 0 = off. */}
                    {intField("exchange_catalogue_refresh_minutes", "rs_catalogue_refresh", "rs_catalogue_refresh_hint")}
                </div>
            </Sub>

            <Sub title={t("rs_invoices")} id="returns-invoices">
                <p className="rt-muted rt-small">{t("rs_invoices_hint")}</p>
                {address("invoice_seller", "name", ["vat_number"])}
            </Sub>

            <Sub title={t("rs_emails")} id="returns-emails">
                <p className="rt-muted rt-small">{t("rs_emails_hint")}</p>
                <div className="rs-grid">
                    {textField("from_address", "rs_from_address", "rs_from_address_hint", { nullable: false })}
                    {textField("reply_to", "rs_reply_to", "rs_reply_to_hint")}
                    {textField("support_inbox_email", "rs_support_inbox", "rs_support_inbox_hint", { nullable: false })}
                    {/* Staff alerts, not a customer email: empty = null = no alert emails. */}
                    {textField("error_alert_email", "rs_error_alert_email", "rs_error_alert_email_hint")}
                </div>
                <div className="rs-checks">
                    {check("shipping_email_returns_link", "rs_shipping_link", "rs_shipping_link_hint")}
                    {check("contact_via_gorgias", "rs_contact_gorgias", "rs_contact_gorgias_hint")}
                </div>
            </Sub>

            <Sub title={t("rs_reasons")} id="returns-reasons">
                <p className="rt-muted rt-small">{t("rs_reasons_hint")}</p>
                <ReasonsEditor t={t} reasons={reasons} setReasons={setReasons} canEdit={canEdit} />
            </Sub>

            <Sub title={t("rs_services")} id="returns-services">
                <p className="rt-muted rt-small">{t("rs_services_hint")}</p>
                <div className="rs-table-wrap">
                    <table className="rs-table rs-table--services">
                        <thead>
                            <tr>
                                <th>{t("rs_on")}</th><th>{t("rs_service")}</th><th>{t("rs_fee")}</th>
                                <th>{t("rs_max_kg")}</th><th>{t("rs_max_len")}</th><th>{t("rs_max_dims")}</th><th>{t("rs_pc_only")}</th><th>{t("rs_pc_never")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {services.map((s) => (
                                <tr key={s.id} className={errors[`services.${s.id}`] ? "rs-row--error" : undefined}>
                                    <td><input type="checkbox" checked={s.enabled} disabled={ro} aria-label={t("rs_service_on", { name: s.name })}
                                        onChange={(e) => setService(s.id, { enabled: e.target.checked })} /></td>
                                    <td>
                                        <strong>{s.carrier}</strong>
                                        <span className="rt-block rt-small rt-muted">{s.name} · <span className="rt-mono">{s.id}</span></span>
                                        {errors[`services.${s.id}`] && <p className="rt-field-error" role="alert">{errors[`services.${s.id}`]}</p>}
                                    </td>
                                    <td>
                                        <ParsedInput key={`fee-${s.id}-${version}`} initial={fmtPounds(s.fee_pence)} parse={penceFrom} prefix="£" inputMode="decimal" width={96}
                                            disabled={ro} ariaLabel={t("rs_fee_for", { name: s.name })} invalid={bad[`fee-${s.id}`]}
                                            onValue={(v) => { markBad(`fee-${s.id}`, v === null); if (v !== null) setService(s.id, { fee_pence: v }); }} />
                                    </td>
                                    <td>
                                        <OptionalLimit key={`kg-${s.id}-${version}`} initial={s.max_kg == null ? "" : String(s.max_kg)} parse={parseNum} width={84}
                                            disabled={ro} ariaLabel={t("rs_max_kg_for", { name: s.name })} placeholder={t("rs_from_carrier")} invalid={bad[`kg-${s.id}`]}
                                            onValue={(v, isBad) => { markBad(`kg-${s.id}`, isBad); if (!isBad) setService(s.id, { max_kg: v as number | undefined }); }} />
                                    </td>
                                    <td>
                                        <OptionalLimit key={`len-${s.id}-${version}`} initial={s.max_len_cm == null ? "" : String(s.max_len_cm)} parse={parseNum} width={84}
                                            disabled={ro} ariaLabel={t("rs_max_len_for", { name: s.name })} placeholder={t("rs_from_carrier")} invalid={bad[`len-${s.id}`]}
                                            onValue={(v, isBad) => { markBad(`len-${s.id}`, isBad); if (!isBad) setService(s.id, { max_len_cm: v as number | undefined }); }} />
                                    </td>
                                    <td>
                                        <OptionalLimit key={`dims-${s.id}-${version}`} initial={fmtDims(s.max_dims_cm)} parse={parseDims} width={120}
                                            disabled={ro} ariaLabel={t("rs_max_dims_for", { name: s.name })} placeholder={t("rs_no_box_limit")} invalid={bad[`dims-${s.id}`]}
                                            onValue={(v, isBad) => { markBad(`dims-${s.id}`, isBad); if (!isBad) setService(s.id, { max_dims_cm: (v as [number, number, number] | undefined) ?? null }); }} />
                                    </td>
                                    <td>
                                        <PatternInput key={`inc-${s.id}-${version}`} initial={s.postcode_include} disabled={ro} placeholder={t("rs_pc_all")}
                                            ariaLabel={t("rs_pc_only_for", { name: s.name })} onValue={(list) => setService(s.id, { postcode_include: list })} />
                                    </td>
                                    <td>
                                        <PatternInput key={`exc-${s.id}-${version}`} initial={s.postcode_exclude} disabled={ro} placeholder={t("rs_pc_none")}
                                            ariaLabel={t("rs_pc_never_for", { name: s.name })} onValue={(list) => setService(s.id, { postcode_exclude: list })} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {err("services")}
                <p className="rt-muted rt-small">{t("rs_limits_hint")}</p>
                <div className="rs-grid">
                    {intField("max_labels", "rs_max_labels", "rs_max_labels_hint")}
                </div>
                <h4 className="rs-h4">{t("rs_inpost_apc")}</h4>
                <div className="rs-grid">
                    {inpostField}
                    {optionalIntField("inpost_drop_off_days", "rs_inpost_drop_off_days", t("rs_inpost_drop_off_days_hint"), t("rs_inpost_default"))}
                    {intField("inpost_locker_radius_km", "rs_inpost_radius", "rs_inpost_radius_hint")}
                    {intField("inpost_lockers_shown", "rs_inpost_lockers_shown")}
                    {intField("depot_dropoff_grace_days", "rs_depot_grace", "rs_depot_grace_hint")}
                </div>
            </Sub>

            <Sub title={t("rm_title")} id="returns-royal-mail-labels">
                <p className="rt-muted rt-small">{t("rm_hint")}</p>
                <div className="rs-grid">
                    {intField("label_pool_low_stock", "rm_low_stock_setting", "rm_low_stock_setting_hint")}
                    {optionalIntField("label_pool_max_age_days", "rm_max_age", t("rm_max_age_hint"), t("rm_no_limit"))}
                    {textField("royal_mail_oba", "rm_oba", "rm_oba_hint", { nullable: false, mono: true })}
                </div>
                <p className="rt-muted rt-small">{t("rm_limits_note")}</p>
                <RoyalMailLabelPool canEdit={canEdit} />
            </Sub>

            <Sub title={t("rs_security")} id="returns-security">
                <div className="rs-grid">
                    {intField("token_account_minutes", "rs_token_account", "rs_token_account_hint")}
                    {intField("token_email_days", "rs_token_email", "rs_token_email_hint")}
                    {intField("session_idle_minutes", "rs_session_idle")}
                    {intField("lookup_ip_failures", "rs_ip_failures", "rs_ip_failures_hint")}
                    {intField("lookup_order_failures", "rs_order_failures", "rs_order_failures_hint")}
                    {intField("captcha_after_failures", "rs_captcha_after")}
                </div>
                <div className="rs-checks">
                    {check("mask_direct_sessions", "rs_mask", "rs_mask_hint")}
                </div>
            </Sub>

            {others.length > 0 && (
                <Sub title={t("rs_other")} id="returns-other">
                    <p className="rt-muted rt-small">{t("rs_other_hint")}</p>
                    <div className="rs-grid">
                        {others.map((k) => {
                            const v = draft[k];
                            if (typeof v === "boolean") return check(k, k);
                            if (typeof v === "number") return (
                                <Field key={k} id={`rs-${k}`} label={k} error={err(k)}>
                                    <ParsedInput key={`${k}-${version}`} id={`rs-${k}`} initial={String(v)} parse={parseNum} inputMode="decimal" disabled={ro}
                                        invalid={!!errors[k] || bad[k]} onValue={(n) => { markBad(k, n === null); if (n !== null) set(k, n); }} />
                                </Field>
                            );
                            if (typeof v === "string" || v === null) return (
                                <Field key={k} id={`rs-${k}`} label={k} error={err(k)}>
                                    <input id={`rs-${k}`} className="rt-input" disabled={ro} value={v ?? ""} onChange={(e) => set(k, e.target.value)} />
                                </Field>
                            );
                            return (
                                <Field key={k} id={`rs-${k}`} label={k} error={err(k)}>
                                    <pre className="rs-json">{JSON.stringify(v, null, 2)}</pre>
                                </Field>
                            );
                        })}
                    </div>
                </Sub>
            )}

            {errors._ && <p className="rt-field-error" role="alert">{errors._}</p>}
            {canEdit && (
                <div className="rs-savebar">
                    <span className="rt-muted rt-small">{changed.length > 0 ? t("rs_unsaved", { n: String(changed.length) }) : t("rs_no_changes")}</span>
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={discard} disabled={saving || changed.length === 0}>
                        <RotateCcw size={14} /> {t("rs_discard")}
                    </button>
                    <button type="button" className="btn btn-sm rt-btn btn-primary" onClick={save} disabled={saving || changed.length === 0}>
                        <Save size={14} /> {saving ? t("saving") : t("rs_save")}
                    </button>
                </div>
            )}
        </div>
    );
};

// ---- small pieces ---------------------------------------------------------------------------

// Closed by default; `reveal` opens one that holds an error or a deep link's target.
const Sub = ({ title, id, children }: { title: string; id: string; children: ReactNode }) => (
    <details className="rs-sub" id={id}>
        <summary><h3>{title}</h3></summary>
        <div className="rs-sub-body">{children}</div>
    </details>
);

const Field = ({ id, label, hint, error, children }: { id: string; label: string; hint?: string; error?: ReactNode; children: ReactNode }) => (
    <div className="rs-field">
        <label className="rt-label" htmlFor={id}>{label}</label>
        {children}
        {hint && <small className="rt-muted">{hint}</small>}
        {error}
    </div>
);

const PatternInput = ({ initial, onValue, disabled, placeholder, ariaLabel }: {
    initial: string[]; onValue: (list: string[]) => void; disabled?: boolean; placeholder?: string; ariaLabel?: string;
}) => {
    const [text, setText] = useState(initial.join(", "));
    return (
        <input className="rt-input rt-mono" value={text} disabled={disabled} placeholder={placeholder} aria-label={ariaLabel}
            onChange={(e) => { setText(e.target.value); onValue(patterns(e.target.value)); }} />
    );
};

const PROBLEMS_WITH_CHOICE = ["return", "damaged"] as const;

const ReasonsEditor = ({ t, reasons, setReasons, canEdit }: {
    t: TT; reasons: ReturnReason[]; setReasons: (r: ReturnReason[]) => void; canEdit: boolean;
}) => {
    const [labels, setLabels] = useState<Record<string, string>>({});
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ code: "", problem: "return", label: "", fault: "customer", needs_detail: false, needs_photo: false });
    const [formError, setFormError] = useState<string | null>(null);
    const [rowError, setRowError] = useState<Record<string, string>>({});

    const replace = (r: ReturnReason) => setReasons(reasons.map((x) => (x.code === r.code ? r : x)));
    const patch = async (code: string, body: Partial<ReturnReason>) => {
        const r = await updateReturnReason(code, body);
        if (r.ok && r.data) { replace(r.data.reason); setRowError((e) => ({ ...e, [code]: "" })); return true; }
        setRowError((e) => ({ ...e, [code]: r.message || t("rs_save_failed") }));
        return false;
    };
    const remove = async (code: string) => {
        if (!window.confirm(t("rs_reason_delete_confirm", { code }))) return;
        const r = await deleteReturnReason(code);
        if (r.ok) setReasons(reasons.filter((x) => x.code !== code));
        else setRowError((e) => ({ ...e, [code]: r.message || t("rs_save_failed") }));
    };
    const add = async () => {
        setFormError(null);
        const r = await createReturnReason({ ...form, code: form.code.trim(), label: form.label.trim() } as any);
        if (r.ok && r.data) {
            setReasons([...reasons, r.data.reason]);
            setAdding(false);
            setForm({ code: "", problem: "return", label: "", fault: "customer", needs_detail: false, needs_photo: false });
        } else setFormError(r.message || t("rs_save_failed"));
    };

    return (
        <>
            <div className="rs-table-wrap">
                <table className="rs-table">
                    <thead>
                        <tr>
                            <th>{t("rs_on")}</th><th>{t("rs_problem")}</th><th>{t("rs_reason_label")}</th><th>{t("rs_fault")}</th>
                            <th>{t("rs_needs_detail")}</th><th>{t("rs_needs_photo")}</th><th aria-label={t("rs_actions")} />
                        </tr>
                    </thead>
                    <tbody>
                        {reasons.map((r) => {
                            const label = labels[r.code] ?? r.label;
                            const dirty = label.trim() !== r.label;
                            return (
                                <tr key={r.code}>
                                    <td><input type="checkbox" checked={r.enabled} disabled={!canEdit || r.fixed} aria-label={t("rs_reason_on", { label: r.label })}
                                        title={r.fixed ? t("rs_reason_fixed") : undefined} onChange={(e) => patch(r.code, { enabled: e.target.checked })} /></td>
                                    <td>{t(`rt_problem_${r.problem}`)}</td>
                                    <td>
                                        <span className="rs-inline">
                                            <input className="rt-input" value={label} disabled={!canEdit} aria-label={t("rs_reason_label")}
                                                onChange={(e) => setLabels((l) => ({ ...l, [r.code]: e.target.value }))} />
                                            {dirty && canEdit && (
                                                <button type="button" className="btn btn-sm rt-btn btn-secondary" onClick={async () => {
                                                    if (await patch(r.code, { label: label.trim() })) setLabels((l) => { const n = { ...l }; delete n[r.code]; return n; });
                                                }}>{t("save")}</button>
                                            )}
                                        </span>
                                        <span className="rt-block rt-small rt-muted rt-mono">{r.code}</span>
                                        {rowError[r.code] && <p className="rt-field-error" role="alert">{rowError[r.code]}</p>}
                                    </td>
                                    <td>
                                        <select className="rt-input" value={r.fault} disabled={!canEdit || r.fixed} aria-label={t("rs_fault")}
                                            onChange={(e) => patch(r.code, { fault: e.target.value as "ours" | "customer" })}>
                                            <option value="ours">{t("rt_fault_ours")}</option>
                                            <option value="customer">{t("rt_fault_customer")}</option>
                                        </select>
                                    </td>
                                    <td><input type="checkbox" checked={r.needs_detail} disabled={!canEdit} aria-label={t("rs_needs_detail")}
                                        onChange={(e) => patch(r.code, { needs_detail: e.target.checked })} /></td>
                                    <td><input type="checkbox" checked={r.needs_photo} disabled={!canEdit} aria-label={t("rs_needs_photo")}
                                        onChange={(e) => patch(r.code, { needs_photo: e.target.checked })} /></td>
                                    <td>
                                        {canEdit && !r.fixed && (
                                            <button type="button" className="rt-icon-btn" aria-label={t("rs_reason_delete", { code: r.code })} onClick={() => remove(r.code)}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {canEdit && !adding && (
                <div className="rs-row"><button type="button" className="btn btn-sm rt-btn btn-secondary" onClick={() => setAdding(true)}><Plus size={14} /> {t("rs_add_reason")}</button></div>
            )}
            {canEdit && adding && (
                <div className="rs-add">
                    <div className="rs-grid">
                        <label className="rs-field"><span className="rt-label">{t("rs_reason_code")}</span>
                            <input className="rt-input rt-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. too_small" /></label>
                        <label className="rs-field"><span className="rt-label">{t("rs_problem")}</span>
                            <select className="rt-input" value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })}>
                                {PROBLEMS_WITH_CHOICE.map((p) => <option key={p} value={p}>{t(`rt_problem_${p}`)}</option>)}
                            </select></label>
                        <label className="rs-field"><span className="rt-label">{t("rs_reason_label")}</span>
                            <input className="rt-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></label>
                        <label className="rs-field"><span className="rt-label">{t("rs_fault")}</span>
                            <select className="rt-input" value={form.fault} onChange={(e) => setForm({ ...form, fault: e.target.value })}>
                                <option value="ours">{t("rt_fault_ours")}</option>
                                <option value="customer">{t("rt_fault_customer")}</option>
                            </select></label>
                    </div>
                    <div className="rs-checks rs-checks--row">
                        <label className="rs-check"><input type="checkbox" checked={form.needs_detail} onChange={(e) => setForm({ ...form, needs_detail: e.target.checked })} /><span>{t("rs_needs_detail")}</span></label>
                        <label className="rs-check"><input type="checkbox" checked={form.needs_photo} onChange={(e) => setForm({ ...form, needs_photo: e.target.checked })} /><span>{t("rs_needs_photo")}</span></label>
                    </div>
                    {formError && <p className="rt-field-error" role="alert">{formError}</p>}
                    <div className="rs-row">
                        <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => { setAdding(false); setFormError(null); }}>{t("cancel")}</button>
                        <button type="button" className="btn btn-sm rt-btn btn-primary" onClick={add}><Plus size={14} /> {t("rs_add_reason")}</button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ReturnsSettings;
